import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTr } from "@/autoTranslate";

/**
 * Perfect Pour — visual espresso pour.
 * Hold Space to pour. Release when the coffee level hits the golden band.
 * Includes 3 rounds and star rating.
 */
export default function PerfectPour({ config, onDone, onClose }) {
  const tr = useTr();
  const [level, setLevel] = useState(0);
  const [pouring, setPouring] = useState(false);
  const [round, setRound] = useState(1);
  const [stars, setStars] = useState([]);
  const [result, setResult] = useState(null); // null | "hit" | "miss"
  const rafRef = useRef();
  const lastRef = useRef(0);

  const totalRounds = 3;
  const zoneStart = 0.65;
  const zoneEnd = 0.82;
  const fillSpeed = 0.42; // per second

  useEffect(() => {
    if (!pouring || result) return;
    const step = (ts) => {
      if (!lastRef.current) lastRef.current = ts;
      const dt = (ts - lastRef.current) / 1000;
      lastRef.current = ts;
      setLevel((L) => {
        const next = Math.min(1, L + fillSpeed * dt);
        if (next >= 1) {
          setPouring(false);
          checkResult(next);
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [pouring, result]);

  const checkResult = (l) => {
    const hit = l >= zoneStart && l <= zoneEnd;
    setResult(hit ? "hit" : "miss");
    const newStars = [...stars, hit];
    setStars(newStars);
    setTimeout(() => {
      if (round >= totalRounds) {
        const score = newStars.filter(Boolean).length;
        if (score >= 2) onDone && onDone(true);
      } else {
        setRound((r) => r + 1);
        setLevel(0);
        setResult(null);
        lastRef.current = 0;
      }
    }, 1200);
  };

  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space" || e.code === "KeyE") e.preventDefault();
      if (result) {
        if (e.code === "Escape") onClose && onClose();
        return;
      }
      if ((e.code === "Space" || e.code === "KeyE") && !e.repeat) {
        if (!pouring && level === 0) {
          setPouring(true);
          lastRef.current = 0;
        }
      }
      if (e.code === "Escape") onClose && onClose();
    };
    const up = (e) => {
      if (e.code !== "Space" && e.code !== "KeyE") return;
      if (result) return;
      if (pouring) {
        setPouring(false);
        cancelAnimationFrame(rafRef.current);
        checkResult(level);
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [pouring, result, level, onClose]);

  const cupInnerW = 140;
  const cupInnerH = 200;
  const totalScore = stars.filter(Boolean).length;
  const finished = round >= totalRounds && result !== null;

  const retry = () => {
    setLevel(0);
    setPouring(false);
    setResult(null);
    setRound(1);
    setStars([]);
    lastRef.current = 0;
  };

  return (
    <div className="w-full max-w-[560px]" data-testid="minigame-timing">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Mini oyun")} · {tr("Tur")} {Math.min(round, totalRounds)}/{totalRounds}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr(config.title)}
          </h3>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: totalRounds }).map((_, i) => (
            <span
              key={i}
              className="text-2xl"
              style={{
                color: stars[i]
                  ? "var(--gold)"
                  : stars[i] === false
                  ? "var(--persimmon)"
                  : "rgba(34,32,46,0.2)",
              }}
              data-testid={`star-${i}`}
            >
              ★
            </span>
          ))}
        </div>
      </div>
      <p className="mt-2 text-sm md:text-base opacity-85">{tr(config.intro)}</p>

      <div className="mt-3 rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--bone-soft)] p-3 text-xs md:text-sm" data-testid="howto-pour">
        <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">{tr("Nasıl oynanır")}</div>
        <p className="mt-1">{tr("Boşluk tuşuna basılı tut, kahve dolmaya başlar. Kahve altın banda gelince tuşu bırak. 3 turdan en az 2'sini tutturursan kazanırsın.")}</p>
      </div>

      <div className="mt-8 flex items-end justify-center gap-8">
        {/* Espresso machine spout */}
        <div className="relative pt-2">
          <div
            className="w-24 h-16 rounded-b-lg border-2 border-[color:var(--ink)]"
            style={{
              background:
                "linear-gradient(180deg, #4a3826 0%, #2a1f14 100%)",
            }}
          />
          <div
            className="mx-auto w-4 h-6 border-2 border-t-0 border-[color:var(--ink)] bg-[color:var(--ink-soft)]"
            style={{ borderBottomLeftRadius: 3, borderBottomRightRadius: 3 }}
          />
          {/* Pour stream */}
          <AnimatePresence>
            {pouring && (
              <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                exit={{ scaleY: 0 }}
                transition={{ duration: 0.2 }}
                className="mx-auto w-1.5 origin-top"
                style={{
                  height: 90,
                  background:
                    "linear-gradient(180deg, #6b4a2f 0%, #3a2818 100%)",
                }}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Cup */}
        <div className="relative" style={{ width: cupInnerW + 40 }}>
          {/* Target zone marker on side gauge */}
          <div
            className="absolute -left-8 rounded-md border-2 border-[color:var(--ink)]"
            style={{
              top: (1 - zoneEnd) * cupInnerH + 20,
              height: (zoneEnd - zoneStart) * cupInnerH,
              width: 6,
              background: "var(--gold)",
            }}
          />
          {/* Cup body */}
          <div
            className="relative mx-auto border-4 border-[color:var(--ink)] rounded-b-3xl bg-[color:var(--paper)] overflow-hidden"
            style={{ width: cupInnerW, height: cupInnerH }}
          >
            {/* Coffee fill */}
            <motion.div
              className="absolute left-0 right-0 bottom-0"
              animate={{ height: `${level * 100}%` }}
              transition={{ duration: 0 }}
              style={{
                background:
                  "linear-gradient(180deg, #6b4a2f 0%, #3a2818 100%)",
              }}
            >
              {/* Foam */}
              {level > 0.02 && (
                <div
                  className="absolute left-0 right-0 top-0 h-2"
                  style={{
                    background:
                      "linear-gradient(180deg, #f4e6d3 0%, rgba(244,230,211,0) 100%)",
                  }}
                />
              )}
            </motion.div>
            {/* Guideline for hint */}
            <div
              className="absolute left-0 right-0 border-t border-dashed"
              style={{
                bottom: `${(zoneStart + zoneEnd) / 2 * 100}%`,
                borderColor: "rgba(34,32,46,0.35)",
              }}
            />
          </div>
          {/* Cup handle */}
          <div
            className="absolute border-4 border-[color:var(--ink)] rounded-full"
            style={{
              width: 44,
              height: 70,
              right: -8,
              top: cupInnerH * 0.25 + 4,
              borderLeft: "none",
              background: "transparent",
            }}
          />
          {/* Saucer */}
          <div
            className="mx-auto mt-1 h-3 rounded-full border-2 border-[color:var(--ink)] bg-[color:var(--paper)]"
            style={{ width: cupInnerW + 20 }}
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-3 text-sm">
        <span className="kbd">{tr("Boşluk")}</span>
        <span className="opacity-70">{tr("basılı tut")}</span>
        <span className="opacity-30">·</span>
        <span className="opacity-70">{tr("köpük altına gelince bırak")}</span>
      </div>

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          data-testid="minigame-pour-hold"
          disabled={!!result}
          onPointerDown={(e) => {
            e.preventDefault();
            if (result || pouring || level !== 0) return;
            setPouring(true);
            lastRef.current = 0;
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            if (result || !pouring) return;
            setPouring(false);
            cancelAnimationFrame(rafRef.current);
            checkResult(level);
          }}
          onPointerLeave={(e) => {
            if (result || !pouring) return;
            setPouring(false);
            cancelAnimationFrame(rafRef.current);
            checkResult(level);
          }}
          className="btn-pill warm select-none touch-none"
          style={{ userSelect: "none" }}
        >
          {pouring ? tr("Dökülüyor… bırak!") : tr("Basılı tut · dök")}
        </button>
      </div>

      <AnimatePresence>
        {result && !finished && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-6 p-3 rounded-md border-2 border-[color:var(--ink)] text-center font-mono-label text-[11px] uppercase tracking-widest ${
              result === "hit"
                ? "bg-[color:var(--grass)]"
                : "bg-[color:var(--persimmon)] text-[color:var(--paper)]"
            }`}
            data-testid={`minigame-round-${result}`}
          >
            {result === "hit" ? tr("Güzel dolum ★") : tr("Kaçırdın — sonraki tur")}
          </motion.div>
        )}
        {finished && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--gold)] text-center"
            data-testid={
              totalScore >= 2 ? "minigame-result-win" : "minigame-result-miss"
            }
          >
            <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("Sonuç")}
            </div>
            <div className="display-serif italic text-2xl">
              {totalScore >= 2 ? tr(config.reward) : tr("Az kaldı. Tekrar dener misin?")}
            </div>
            <div className="mt-3 flex justify-center gap-2">
              <button
                onClick={retry}
                className="btn-pill sun"
                data-testid="minigame-retry"
              >
                {tr("Tekrar oyna")}
              </button>
              <button
                onClick={onClose}
                className="btn-pill ghost"
                data-testid="minigame-close"
              >
                {tr("Çık")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
